import { Check, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { AdminPill } from "@/components/shell/AdminPage";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { userAvatarInitial, userDisplayLabel } from "@/lib/user-display";
import { cn } from "@/lib/utils";

/** 账户成员（选人列表用；与 memberships API 形状对齐） */
export type AccountMember = {
  id: number;
  user_id: number;
  role: "owner" | "admin" | "member";
  kind: "internal" | "external";
  disabled: boolean;
  user: {
    display_name: string | null;
    display_label?: string | null;
    email: string | null;
    phone: string | null;
    avatar_url: string | null;
    avatar_initial: string;
  };
};

const roleLabels: Record<AccountMember["role"], string> = {
  owner: "所有者",
  admin: "管理员",
  member: "成员",
};

type AccountMemberPickerBaseProps = {
  members: AccountMember[];
  loading?: boolean;
  disabled?: boolean;
  emptyText?: string;
  searchPlaceholder?: string;
  className?: string;
};

type AccountMemberPickerSingleProps = AccountMemberPickerBaseProps & {
  mode: "single";
  value: number | null;
  onChange: (value: number | null) => void;
};

type AccountMemberPickerMultipleProps = AccountMemberPickerBaseProps & {
  mode: "multiple";
  value: number[];
  onChange: (value: number[]) => void;
};

export type AccountMemberPickerProps =
  | AccountMemberPickerSingleProps
  | AccountMemberPickerMultipleProps;

/**
 * 账户成员选人：受控、不拉数；选人键为 user_id。
 * 单选点行即选中；多选可勾选/取消。
 */
export function AccountMemberPicker(props: AccountMemberPickerProps) {
  const {
    mode,
    members,
    loading = false,
    disabled = false,
    emptyText = "暂无可用成员",
    searchPlaceholder = "搜索昵称、邮箱、手机、角色…",
    className,
  } = props;

  const [query, setQuery] = useState("");

  const selectedSet = useMemo(() => {
    if (mode === "multiple") return new Set(props.value);
    return new Set(props.value != null ? [props.value] : []);
  }, [mode, props.value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter((membership) => {
      const u = membership.user;
      const label = userDisplayLabel({
        id: membership.user_id,
        display_name: u.display_name,
        email: u.email,
        phone: u.phone,
      }).toLowerCase();
      const haystack = [label, u.email, u.phone, u.display_name, String(membership.user_id), roleLabels[membership.role]]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [members, query]);

  function selectSingle(userId: number) {
    if (disabled || loading) return;
    if (mode !== "single") return;
    props.onChange(userId);
  }

  function toggleMultiple(userId: number) {
    if (disabled || loading) return;
    if (mode !== "multiple") return;
    const next = new Set(props.value);
    if (next.has(userId)) next.delete(userId);
    else next.add(userId);
    props.onChange([...next]);
  }

  return (
    <div className={cn("flex min-h-0 flex-col gap-3", className)}>
      <div className="relative shrink-0">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder}
          className="pl-9"
          aria-label="搜索成员"
          disabled={loading || disabled}
        />
      </div>

      <div className="min-h-0 max-h-72 flex-1 overflow-y-auto rounded-xl border border-border sm:max-h-80">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">加载成员…</p>
        ) : filtered.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">
            {members.length === 0 ? emptyText : "无匹配成员"}
          </p>
        ) : (
          <ul
            className="divide-y divide-border"
            role="listbox"
            aria-multiselectable={mode === "multiple"}
            aria-label="可选成员"
          >
            {filtered.map((membership) => {
              const u = membership.user;
              const checked = selectedSet.has(membership.user_id);
              const label = userDisplayLabel({
                id: membership.user_id,
                display_name: u.display_name ?? u.display_label,
                email: u.email,
                phone: u.phone,
              });
              const initial = userAvatarInitial({
                email: u.email,
                display_name: u.display_name,
                avatar_initial: u.avatar_initial,
              });
              return (
                <li key={membership.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={checked}
                    disabled={disabled || loading}
                    onClick={() =>
                      mode === "single"
                        ? selectSingle(membership.user_id)
                        : toggleMultiple(membership.user_id)
                    }
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50 disabled:pointer-events-none disabled:opacity-50",
                      checked && "bg-primary/5",
                    )}
                  >
                    <Avatar className="size-10 shrink-0">
                      {u.avatar_url ? <AvatarImage src={u.avatar_url} alt={label} /> : null}
                      <AvatarFallback className="bg-primary/10 text-sm font-medium text-primary">
                        {initial}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-medium">{label}</span>
                        <AdminPill>{roleLabels[membership.role]}</AdminPill>
                      </div>
                      <div className="space-y-0.5 text-xs text-muted-foreground">
                        <div className="truncate">邮箱：{u.email || "—"}</div>
                        <div className="truncate">手机：{u.phone || "—"}</div>
                      </div>
                    </div>
                    <span
                      className={cn(
                        "flex size-4 shrink-0 items-center justify-center border border-border",
                        mode === "single" ? "rounded-full" : "rounded",
                        checked && "border-primary bg-primary text-primary-foreground",
                      )}
                      aria-hidden
                    >
                      {checked ? <Check className="size-3" /> : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
